import React, { useState, useEffect } from 'react';
import { 
    Container, 
    Typography, 
    Button, 
    Grid, 
    Paper, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow,
    TextField,
    IconButton,
    Box,
    Divider,
    Chip,
    Card,
    CardContent,
    Tabs,
    Tab,
    Dialog,
    DialogTitle,
    DialogContent,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    useTheme,
    Avatar,
    Badge,
    CardMedia
} from '@mui/material';
import { 
    Add, 
    Remove, 
    Delete, 
    ShoppingCart, 
    Search, 
    Person,
    Close, 
    Category as CategoryIcon, 
    Close as CloseIcon, 
    MergeType,
    History,
    MeetingRoom
} from '@mui/icons-material';
import useCustomToast from '../../../hooks/useToast';
import axios from 'axios';
import { TEXT_CONNECTION_ERROR, TEXT_DATA_LOADED } from '../../../services/Text';
import { 
    APIOrderStore, 
    APIFoodCategoryNames, 
    APCustomerTypeNames,
    APIFoodNames,
    APIEmployeeNames,
    APIPositionNames,
    APIPosKitchenListBy,
    APICustomerPendingOrders,
    APIRoomNames,
    APIBookingNames
} from '../../../services/APIs';
import { 
    formatNumberWithComma, 
    getCategoriesNameById,
    getFoodImageUrl,
    getUserDetails,
    getUserRole,
} from '../../../utils/helpers';
import PrintReceipt from './PrintReceipt';
import { useNavigate, useLocation } from 'react-router-dom';
import { foodTypes } from '../../../utils/dataInfo';
import { PERMISSIONS, ROLE_PERMISSIONS } from '../../../utils/Permission';
import { apiService } from '../../../apiServices/ApiService';

const Pos = () => {
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const [processingOrder, setProcessingOrder] = useState(false);
    const [error, setError] = useState(null);
    const theme = useTheme();
    const showToast = useCustomToast();

    const [pageState, setPageState] = useState({
        productsData: [],
        categoriesData: [],
        pendingOrders: [],
    });

    const [cart, setCart] = useState([]);
    const [foodData, setFoodData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedFoodType, setSelectedFoodType] = useState('ki'); // Default to Kitchen
    const [selectedCategory, setSelectedCategory] = useState('');
    const [showPendingOrders, setShowPendingOrders] = useState(false);
    const [selectedPendingOrder, setSelectedPendingOrder] = useState(null);
    const [manualCustomerInput, setManualCustomerInput] = useState(''); // For manual customer name input

    // Form states
    const [formData, setFormData] = useState({
        customer_id: '',
        customer_name: '', // Added customer_name field
        employee_position_id: '',
        customer_type_id: '',
        servant_id: '',
        room_number_id: '',
        previousOrderNumber: '',
        order_notes: '',
        parent_order_id: ''
    });

    const [referenceData, setReferenceData] = useState({
        customerType: [],
        employeeNames: [],
        positionNames: [],
        roomNumbers: [],
        bookedCustomers: [], // Added booked customers
    });

    const [orderSettingsTab, setOrderSettingsTab] = useState(0);
    const [customerEditable, setCustomerEditable] = useState(true);
    const [selectedRoomBookedCustomers, setSelectedRoomBookedCustomers] = useState([]); // Customers for selected room

    // Permissions
    const [userRole, setUserRole] = useState('');

    useEffect(() => {
        const role = getUserRole();
        setUserRole(role);
    }, []);

    useEffect(() => {
        apiService.setToastFunction(showToast);
    }, [showToast]);

    const hasPermission = (permission) => {
        return ROLE_PERMISSIONS[userRole]?.includes(permission) || false;
    };

    useEffect(() => {
        fetchData();
        fetchReferenceData();
        fetchFoodData();
    }, [selectedFoodType]);

    // Get customer data from navigation state
    useEffect(() => {
        if (location.state) {
            const { customerId, customerTypeId, previousOrderNumber, roomNumberId, customerName } = location.state;
            if (customerId) {
                setFormData(prev => ({
                    ...prev,
                    customer_id: customerId,
                    customer_name: customerName || '',
                    customer_type_id: customerTypeId || '',
                    room_number_id: roomNumberId || '',
                    previousOrderNumber: previousOrderNumber || ''
                }));
                setCustomerEditable(false);
                setManualCustomerInput(customerName || '');
                
                // Fetch pending orders for this customer
                fetchCustomerPendingOrders(customerId);
                
                showToast("Info", `Adding order for customer: ${customerName || customerId}`, "info");
            }
        }
    }, [location]);

    const fetchCustomerPendingOrders = async (customerId) => {
        try {
            const response = await axios.get(APICustomerPendingOrders, {
                params: { customer_id: customerId }
            });
            
            if (response.data.status === 'success') {
                // Filter for pending orders only
                const pendingOrders = (response.data.data || []).filter(
                    order => order.order_status === 'pending'
                );
                
                setPageState(prev => ({
                    ...prev,
                    pendingOrders: pendingOrders
                }));
                
                if (pendingOrders.length > 0) {
                    setShowPendingOrders(true);
                }
            }
        } catch (error) {
            console.error('Error fetching pending orders:', error);
        }
    };

    const fetchReferenceData = async () => {
        try {
            const [empResult, posResult, typResult, roomsRes, bookedRes] = await Promise.all([
                apiService.listNames(APIEmployeeNames),
                apiService.listNames(APIPositionNames),
                apiService.listNames(APCustomerTypeNames),
                apiService.listNames(APIRoomNames),
                apiService.listNames(APIBookingNames),
            ]);

            setReferenceData({
                employeeNames: empResult.success ? empResult.data : [],
                positionNames: posResult.success ? posResult.data : [],
                customerType: typResult.success ? typResult.data : [],
                roomNumbers: roomsRes.success ? roomsRes.data : [],
                bookedCustomers: bookedRes.success ? bookedRes.data : [],
            });
        } catch (error) {
            showToast("Error", "Failed to load reference data", "error");
        }
    };

    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
            const branchIdToUse = getUserDetails("branchId");

            // Fetch products based on food type
            const params = {};
            if (branchIdToUse) {
                params.branch_id = branchIdToUse;
            }

            const productsRes = await axios.get(`${APIPosKitchenListBy}${selectedFoodType}`, { params });

            // Fetch categories
            const categoriesRes = await axios.get(APIFoodCategoryNames);

            // Process data
            const products = Array.isArray(productsRes.data.data)
                ? productsRes.data.data.reverse() : [];

            const categories = Array.isArray(categoriesRes.data.data) 
                ? categoriesRes.data.data.reverse() : [];

            setPageState(prev => ({
                ...prev,
                productsData: products,
                categoriesData: categories,
            }));

            showToast("Success", TEXT_DATA_LOADED, "success");
        } catch (error) {
            let errorMessage = "Failed to load data";
            if (error.response) {
                errorMessage = error.response.data.message || error.message;
            } else if (error.code === "ERR_NETWORK") {
                errorMessage = TEXT_CONNECTION_ERROR;
            }
            setError(errorMessage);
            showToast("Error", errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    const fetchFoodData = async () => {
        try {
            const response = await axios.get(APIFoodNames);
            if (response.data.status === 'success') {
                setFoodData(response.data.data || []);
            }
        } catch (error) {
            console.error("Error fetching food data:", error);
        }
    };

    // Filter products based on search and category
    const filteredProducts = pageState.productsData.filter(product => {
        const matchesSearch = searchTerm === '' || 
            product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            product.description?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const productCategoryStr = product.food_category_id ? product.food_category_id.toString() : '';
        const selectedCategoryStr = selectedCategory.toString();
        
        const matchesCategory = selectedCategoryStr === '' || 
            productCategoryStr === selectedCategoryStr;
        
        return matchesSearch && matchesCategory;
    });

    // Form handlers
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle room number change - fetch booked customers for that room
    const handleRoomNumberChange = async (roomId) => {
        setFormData(prev => ({
            ...prev,
            room_number_id: roomId,
            customer_id: '', // Clear customer when room changes
            customer_name: '' // Clear customer name
        }));
        setManualCustomerInput('');

        if (roomId) {
            // Fetch booked customers for this room
            try {
                const response = await axios.get(APIBookingNames, {
                    params: { room_id: roomId }
                });
                if (response.data.status === 'success') {
                    setSelectedRoomBookedCustomers(response.data.data);
                    if (response.data.data.length === 0) {
                        showToast("Info", "No customers checked in this room", "info");
                    }
                } else {
                    setSelectedRoomBookedCustomers([]);
                }
            } catch (error) {
                console.error('Error fetching room customers:', error);
                setSelectedRoomBookedCustomers([]);
            }
        } else {
            setSelectedRoomBookedCustomers([]);
        }
    };

    // Handle customer selection from booked customers
    const handleCustomerSelect = (customer) => {
        setFormData(prev => ({
            ...prev,
            customer_id: customer.id,
            customer_name: customer.customer_name
        }));
        setManualCustomerInput(customer.customer_name);
    };

    // Handle manual customer name input (when no room selected or adding new customer)
    const handleManualCustomerChange = (value) => {
        setManualCustomerInput(value);
        setFormData(prev => ({
            ...prev,
            customer_name: value,
            customer_id: '' // Clear ID since this is a new/manual customer
        }));
    };

    // Cart handlers
    const addToCart = (product) => {
        const existingItem = cart.find(item => item.id === product.id);
        if (existingItem) {
            if (existingItem.quantity < product.quantity) {
                setCart(cart.map(item =>
                    item.id === product.id
                        ? { ...item, quantity: item.quantity + 1 }
                        : item
                ));
            } else {
                showToast("Stock Limit", "Cannot add more than available quantity", "warning");
            }
        } else {
            setCart([...cart, { 
                ...product, 
                quantity: 1,
                price: product.price || product.price_per_unit,
                food_type_id: product.food_type_id || selectedFoodType
            }]);
        }
    };

    const removeFromCart = (productId) => {
        setCart(cart.filter(item => item.id !== productId));
    };

    const adjustQuantity = (productId, adjustment) => {
        setCart(cart.map(item => {
            if (item.id === productId) {
                const newQuantity = item.quantity + adjustment;
                if (newQuantity > 0) {
                    return { ...item, quantity: newQuantity };
                }
            }
            return item;
        }));
    };

    const calculateTotal = () => {
        return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
    };

    const validateForm = () => {
        const errors = [];
        if (cart.length === 0) {
            errors.push("Cart is empty");
        }
        if (!formData.servant_id) {
            errors.push("Servant is required");
        }
        if (!formData.customer_name && !formData.customer_id) {
            errors.push("Customer name is required");
        }

        if (errors.length > 0) {
            showToast("Validation Error", errors.join('\n'), "warning");
            return false;
        }
        return true;
    };

    const handleCombineOrder = (pendingOrder) => {
        setSelectedPendingOrder(pendingOrder);
        setFormData(prev => ({
            ...prev,
            previousOrderNumber: pendingOrder.order_number,
            room_number_id: pendingOrder.room_number_id || prev.room_number_id
        }));
        setShowPendingOrders(false);
        
        showToast(
            "Info", 
            `Adding items to existing order #${pendingOrder.order_number}. Current total: ${formatNumberWithComma(pendingOrder.total_amount)}`, 
            "info"
        );
    };

    const handleCheckout = async () => {
        if (!validateForm()) return;

        setProcessingOrder(true);

        // Prepare order data
        const orderData = {
            user_id: getUserDetails("Id"),
            customer_id: formData.customer_name,
            customer_type_id: formData.customer_type_id,
            booked_customer_id: formData.customer_id || null, // Get from selected customer if selected else null
            servant_id: formData.servant_id,
            room_number_id: formData.room_number_id || null,
            order_notes: formData.order_notes,
            previousOrderNumber: formData.previousOrderNumber,
            order_status: 'pending',
            total_amount: calculateTotal(),
            items: cart.map(item => ({
                food_id: item.id,
                quantity: item.quantity,
                price: item.price,
                food_type_id: item.food_type_id,
                total: item.price * item.quantity
            })),
            branch_id: getUserDetails("branchId"),
            parent_order_id: formData.parent_order_id
        };

        try {
            const response = await axios.post(APIOrderStore, orderData);

            if (response.data.status === "success") {
                // Reset cart and form
                setCart([]);
                setFormData({
                    customer_id: '',
                    customer_name: '',
                    employee_position_id: '',
                    customer_type_id: '',
                    servant_id: '',
                    room_number_id: '',
                    order_notes: '',
                    previousOrderNumber: '',
                    parent_order_id: ''
                });
                setManualCustomerInput('');
                setCustomerEditable(true);
                setSelectedPendingOrder(null);
                setSelectedRoomBookedCustomers([]);

                const message = response.data.is_combined 
                    ? `Items added to existing order #${response.data.order_number}`
                    : "Order completed successfully!";
                
                showToast("Success", message, "success");
                
                // Refresh product data
                fetchData();
                
                // Navigate back to order list if this was a new order from existing customer
                if (formData.parent_order_id) {
                    navigate('/orders');
                }
            } else {
                showToast("Error", response.data.message || "Failed to complete order", "error");
            }
        } catch (error) {
            console.error("Checkout error:", error);
            let errorMessage = "Failed to complete order";
            if (error.response) {
                errorMessage = error.response.data.message || error.message;
            } else if (error.code === "ERR_NETWORK") {
                errorMessage = TEXT_CONNECTION_ERROR;
            }
            showToast("Error", errorMessage, "error");
        } finally {
            setProcessingOrder(false);
        }
    };

    const handleOrderSettingsTabChange = (event, newValue) => {
        setOrderSettingsTab(newValue);
    };

    const handleClearAllFilters = () => {
        setSelectedCategory('');
        setSearchTerm('');
    };

    const getInitials = (name) => {
        if (!name) return '??';
        return name
            .split(' ')
            .map(word => word[0])
            .join('')
            .toUpperCase()
            .substring(0, 2);
    };

    if (loading) return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <CircularProgress />
        </Box>
    );

    if (error) return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <Typography color="error">Error: {error}</Typography>
        </Box>
    );

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ShoppingCart color="primary" sx={{ fontSize: 20, mr: 2 }} />
                    <Typography sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        Restaurant Point of Sale
                    </Typography>
                </Box>
                
                {formData.previousOrderNumber && (
                    <Chip
                        icon={<MergeType />}
                        label={`Combining with order #${formData.previousOrderNumber}`}
                        color="info"
                        onDelete={() => {
                            setFormData(prev => ({ ...prev, previousOrderNumber: '' }));
                            setSelectedPendingOrder(null);
                        }}
                    />
                )}
            </Box>

            {/* Pending Orders Dialog */}
            <Dialog 
                open={showPendingOrders} 
                onClose={() => setShowPendingOrders(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center">
                            <History sx={{ mr: 1 }} />
                            Pending Orders for this Customer
                        </Box>
                        <IconButton onClick={() => setShowPendingOrders(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        You have pending orders for this customer. You can add items to an existing order or create a new one.
                    </Typography>
                    
                    <Grid container spacing={2}>
                        {pageState.pendingOrders.map((order) => (
                            <Grid item xs={12} key={order.id}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} md={4}>
                                                <Typography variant="subtitle2">
                                                    Order #{order.order_number}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {new Date(order.created_at).toLocaleString()}
                                                </Typography>
                                                {order.room_number && (
                                                    <Typography variant="caption" color="primary" display="block">
                                                        Room: {order.room_number}
                                                    </Typography>
                                                )}
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <Typography variant="body2">
                                                    Items: {order.order_items?.length || 0}
                                                </Typography>
                                                <Typography variant="body2">
                                                    Total: {formatNumberWithComma(order.total_amount)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <Button
                                                    fullWidth
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={() => handleCombineOrder(order)}
                                                    startIcon={<MergeType />}
                                                >
                                                    Add to this Order
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                        
                        <Grid item xs={12}>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => setShowPendingOrders(false)}
                            >
                                Create New Order Instead
                            </Button>
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>

            <Grid container spacing={3} sx={{ display: 'flex', flexWrap: 'nowrap' }}>
                {/* Products List - Left Side */}
                <Grid item xs={12} md={8} sx={{ flex: '0 0 60%' }}>
                    <Paper elevation={2} sx={{ p: 3, borderRadius: 3, height: '100%', background: '#F5F5F0' }}>
                        {/* Search and Filter Section */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            {/* Food Type Filter */}
                            <Grid item xs={12} sm={4}>
                                <Paper
                                    variant="outlined" 
                                    sx={{ 
                                        p: '4px 8px', 
                                        borderRadius: '8px',
                                        display: 'flex', 
                                        alignItems: 'center',
                                        backgroundColor: theme.palette.primary.light
                                    }}
                                >
                                    <CategoryIcon sx={{ color: 'white', mr: 1 }} />
                                    <FormControl variant="standard" sx={{ minWidth: 120 }}>
                                        <Select
                                            value={selectedFoodType}
                                            onChange={(e) => setSelectedFoodType(e.target.value)}
                                            displayEmpty
                                            sx={{
                                                color: 'white',
                                                '&:before, &:after': { borderBottom: 'none' },
                                                '& .MuiSelect-select': {
                                                    paddingRight: '24px',
                                                },
                                                '& .MuiSvgIcon-root': {
                                                    color: 'white',
                                                }
                                            }}
                                            inputProps={{ 'aria-label': 'Select POS type' }}
                                        >
                                            {foodTypes.map((type) => (
                                                <MenuItem key={type.id} value={type.id}>
                                                    {type.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Paper>
                            </Grid>
                            
                            {/* Category Filter */}
                            <Grid item xs={12} sm={4}>
                                <Paper
                                    variant="outlined" 
                                    sx={{ 
                                        p: '4px 8px', 
                                        borderRadius: '8px',
                                        display: 'flex', 
                                        alignItems: 'center',
                                        backgroundColor: theme.palette.primary.light
                                    }}
                                >
                                    <CategoryIcon sx={{ color: 'white', mr: 1 }} />
                                    <FormControl variant="standard" sx={{ minWidth: 120 }}>
                                        <Select
                                            value={selectedCategory}
                                            onChange={(e) => setSelectedCategory(e.target.value)}
                                            displayEmpty
                                            sx={{
                                                color: 'white',
                                                '&:before, &:after': { borderBottom: 'none' },
                                                '& .MuiSelect-select': {
                                                    paddingRight: '24px',
                                                },
                                                '& .MuiSvgIcon-root': {
                                                    color: 'white',
                                                }
                                            }}
                                            inputProps={{ 'aria-label': 'Select category' }}
                                        >
                                            <MenuItem value="">
                                                <em>All Categories</em>
                                            </MenuItem>
                                            {pageState.categoriesData.map((category) => (
                                                <MenuItem key={category.id} value={category.id.toString()}>
                                                    {category.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Paper>
                            </Grid>
                            
                            {/* Search Bar */}
                            <Grid item xs={12} sm={4}>
                                <Paper sx={{
                                    p: '2px 8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    borderRadius: '8px',
                                    backgroundColor: theme.palette.primary.light
                                }}>
                                    <Search sx={{ mr: 1, color: 'white' }} />
                                    <TextField
                                        fullWidth
                                        variant="standard"
                                        placeholder="Search Products ..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        sx={{
                                            '& .MuiInput-underline:before': { borderBottom: 'none' },
                                            '& .MuiInput-underline:after': { borderBottom: 'none' },
                                            fontSize: '0.875rem',
                                            color: 'white',
                                            '& input': {
                                                color: 'white',
                                                '&::placeholder': {
                                                    color: 'rgba(255, 255, 255, 0.7)',
                                                    opacity: 1
                                                }
                                            }
                                        }}
                                        InputProps={{
                                            endAdornment: searchTerm && (
                                                <IconButton 
                                                    size="small" 
                                                    onClick={() => setSearchTerm('')}
                                                    sx={{ color: 'white' }}
                                                >
                                                    <Close fontSize="small" />
                                                </IconButton>
                                            )
                                        }}
                                        size="small"
                                    />
                                </Paper>
                            </Grid>

                            {/* Clear All Filters Button */}
                            {(selectedCategory || searchTerm) && (
                                <Grid item xs={12}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<Close />}
                                        onClick={handleClearAllFilters}
                                        size="small"
                                        sx={{
                                            textTransform: 'none',
                                            borderRadius: '8px',
                                            mt: 1
                                        }}
                                    >
                                        Clear All Filters
                                    </Button>
                                </Grid>
                            )}
                        </Grid>

                        {/* Products Grid */}
                        <Grid container spacing={2}>
                            {filteredProducts.map((product) => {
                                const imageUrl = getFoodImageUrl(product.image);
                                return (
                                    <Grid item xs={6} sm={4} md={3} key={product.id}>
                                        <Card
                                            elevation={1}
                                            sx={{
                                                cursor: 'pointer',
                                                borderRadius: 2,
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    backgroundColor: 'primary.light',
                                                    color: 'white',
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: 3
                                                }
                                            }}
                                            onClick={() => addToCart(product)}
                                        >
                                            <CardContent sx={{ p: 1.5 }}>
                                                {imageUrl ? (
                                                    <CardMedia
                                                        component="img"
                                                        height="80"
                                                        image={imageUrl}
                                                        alt={product.name}
                                                        sx={{ borderRadius: 1, mb: 1 }}
                                                    />
                                                ) : (
                                                    <Avatar
                                                        sx={{ 
                                                            width: 80, 
                                                            height: 80, 
                                                            mb: 1,
                                                            mx: 'auto',
                                                            bgcolor: 'grey.300',
                                                            fontSize: '14px'
                                                        }}
                                                    >
                                                        {getInitials(product.name)}
                                                    </Avatar>
                                                )}
                                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
                                                    {getCategoriesNameById(product.food_category_id, pageState.categoriesData) || 'Uncategorized'}
                                                </Typography>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                    {product.name || getCategoriesNameById(product.food_id, foodData)}
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                        {formatNumberWithComma(product.price || product.price_per_unit)}
                                                    </Typography>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                );
                            })}

                            {filteredProducts.length === 0 && (
                                <Grid item xs={12}>
                                    <Box sx={{
                                        textAlign: 'center',
                                        py: 6,
                                        border: `1px dashed ${theme.palette.divider}`,
                                        borderRadius: 2
                                    }}>
                                        <Search sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                                        <Typography color="text.secondary" gutterBottom>
                                            No products found
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {searchTerm || selectedCategory ? 
                                                "Try adjusting your search or filter criteria" : 
                                                "No products available in stock"}
                                        </Typography>
                                        {(searchTerm || selectedCategory) && (
                                            <Button
                                                variant="outlined"
                                                startIcon={<Close />}
                                                onClick={handleClearAllFilters}
                                                size="small"
                                                sx={{ mt: 2 }}
                                            >
                                                Clear Filters
                                            </Button>
                                        )}
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </Paper>
                </Grid>

                {/* Order Summary and Settings - Right Side */}
                <Grid item xs={12} md={8} sx={{ flex: '0 0 40%' }}>
                    <Paper elevation={2} sx={{ p: 3, borderRadius: 3, height: '100%' }}>
                        <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
                            <ShoppingCart sx={{ verticalAlign: 'middle', mr: 1 }} />
                            Order Summary
                            {selectedPendingOrder && (
                                <Chip 
                                    size="small"
                                    label={`Combining with #${selectedPendingOrder.order_number}`}
                                    color="info"
                                    sx={{ ml: 1 }}
                                />
                            )}
                        </Typography>

                        {/* Tabs for Order Settings */}
                        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                            <Tabs 
                                value={orderSettingsTab} 
                                onChange={handleOrderSettingsTabChange}
                                variant="fullWidth"
                            >
                                <Tab label="Customer Info" />
                                <Tab label="Order Settings" />
                                <Tab label={
                                    <Badge badgeContent={cart.length} color="primary">
                                        Cart & Payment
                                    </Badge>
                                } />
                            </Tabs>
                        </Box>

                        {/* Customer Info Tab */}
                        {orderSettingsTab === 0 && (
                            <Box>
                                {/* Room Number Selection */}
                                <Box sx={{ mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <MeetingRoom color="action" sx={{ mr: 1 }} />
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Room Number (Optional)</InputLabel>
                                            <Select
                                                value={formData.room_number_id}
                                                onChange={(e) => handleRoomNumberChange(e.target.value)}
                                                name="room_number_id"
                                                label="Room Number (Optional)"
                                            >
                                                <MenuItem value="">
                                                    <em>No Room Assigned</em>
                                                </MenuItem>
                                                {referenceData.roomNumbers.map((room) => (
                                                    <MenuItem key={room.id} value={room.id}>
                                                        Room - {room.room_number || room.roomtype || room.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                </Box>

                                {/* Customer Selection - Conditionally render based on room selection */}
                                {formData.room_number_id ? (
                                    // Show booked customers if room is selected
                                    <Box sx={{ mb: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Person color="action" sx={{ mr: 1 }} />
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Select Checked-in Customer</InputLabel>
                                                <Select
                                                    value={formData.customer_id}
                                                    onChange={(e) => {
                                                        const selectedCustomer = selectedRoomBookedCustomers.find(
                                                            c => c.id === e.target.value
                                                        );
                                                        if (selectedCustomer) {
                                                            handleCustomerSelect(selectedCustomer);
                                                        }
                                                    }}
                                                    label="Select Checked-in Customer"
                                                    disabled={selectedRoomBookedCustomers.length === 0}
                                                >
                                                    <MenuItem value="">
                                                        <em>
                                                            {selectedRoomBookedCustomers.length === 0 
                                                                ? "No customers checked in this room" 
                                                                : "Select a customer"}
                                                        </em>
                                                    </MenuItem>
                                                    {selectedRoomBookedCustomers.map((customer) => (
                                                        <MenuItem key={customer.id} value={customer.id}>
                                                            {customer.customer_name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Box>
                                        {selectedRoomBookedCustomers.length === 0 && formData.room_number_id && (
                                            <Typography variant="caption" color="warning.main" sx={{ ml: 4 }}>
                                                No customers are checked into this room. You can manually enter a customer name below.
                                            </Typography>
                                        )}
                                    </Box>
                                ) : null}

                                {/* Manual Customer Input - Show when no room selected OR no booked customers in selected room */}
                                {(!formData.room_number_id || selectedRoomBookedCustomers.length === 0) && (
                                    <Box sx={{ mb: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Person color="action" sx={{ mr: 1 }} />
                                            <TextField
                                                fullWidth
                                                label="Customer Name *"
                                                variant="outlined"
                                                value={manualCustomerInput}
                                                onChange={(e) => handleManualCustomerChange(e.target.value)}
                                                size="small"
                                                placeholder="Enter customer name"
                                                helperText={formData.room_number_id ? "No checked-in customer found. Enter name manually." : "Enter customer name"}
                                            />
                                        </Box>
                                    </Box>
                                )}

                                {/* Display selected customer info when room has customers */}
                                {formData.room_number_id && formData.customer_name && selectedRoomBookedCustomers.length > 0 && (
                                    <Box sx={{ mb: 2, ml: 4 }}>
                                        <Chip
                                            label={`Selected: ${formData.customer_name}`}
                                            color="primary"
                                            size="small"
                                            onDelete={() => {
                                                setFormData(prev => ({ ...prev, customer_id: '', customer_name: '' }));
                                                setManualCustomerInput('');
                                            }}
                                        />
                                    </Box>
                                )}

                                {/* Customer Type Selection */}
                                <Box sx={{ mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <CategoryIcon color="action" sx={{ mr: 1 }} />
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Customer Type</InputLabel>
                                            <Select
                                                value={formData.customer_type_id}
                                                onChange={handleInputChange}
                                                name="customer_type_id"
                                                label="Customer Type"
                                            >
                                                <MenuItem value="">
                                                    <em>Select Customer Type</em>
                                                </MenuItem>
                                                {referenceData.customerType.map((type) => (
                                                    <MenuItem key={type.id} value={type.id}>
                                                        {type.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {/* Order Settings Tab */}
                        {orderSettingsTab === 1 && (
                            <Box>
                                <Grid item xs={12}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Employee Position *</InputLabel>
                                        <Select
                                            name="employee_position_id"
                                            value={formData.employee_position_id || ''}
                                            onChange={(e) => {
                                                const positionId = e.target.value;
                                                setFormData(prev => ({
                                                    ...prev,
                                                    employee_position_id: positionId,
                                                    servant_id: '' // Clear servant selection when position changes
                                                }));
                                            }}
                                            label="Employee Position *"
                                            required
                                        >
                                            <MenuItem value="">
                                                <em>Select employee position</em>
                                            </MenuItem>
                                            {referenceData.positionNames.map((position) => (
                                                <MenuItem key={position.id} value={position.id}>
                                                    {position.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <br/>
                                <Grid item xs={12}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Servant *</InputLabel>
                                        <Select
                                            name="servant_id"
                                            value={formData.servant_id}
                                            onChange={handleInputChange}
                                            label="Servant *"
                                            required
                                            disabled={!formData.employee_position_id}
                                        >
                                            <MenuItem value="">
                                                <em>
                                                    {formData.employee_position_id
                                                        ? 'Select a Servant'
                                                        : 'Please select position first'}
                                                </em>
                                            </MenuItem>
                                            {referenceData.employeeNames
                                                .filter(employee =>
                                                    formData.employee_position_id
                                                        ? employee.position_id === formData.employee_position_id
                                                        : true
                                                )
                                                .map((employee) => (
                                                    <MenuItem key={employee.id} value={employee.id}>
                                                        {employee.employee_name || employee.name}
                                                    </MenuItem>
                                                ))
                                            }
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <br/>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Order Notes (Optional)"
                                        variant="outlined"
                                        value={formData.order_notes}
                                        name="order_notes"
                                        onChange={handleInputChange}
                                        multiline
                                        rows={3}
                                        size="small"
                                    />
                                </Grid>
                            </Box>
                        )}

                        {/* Cart & Payment Tab */}
                        {orderSettingsTab === 2 && (
                            <Box>
                                {/* Cart Items */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography sx={{ mb: 2, fontWeight: 'bold' }}>
                                        Cart Items ({cart.length})
                                    </Typography>
                                    <TableContainer sx={{ maxHeight: 200 }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                                                    <TableCell sx={{ color: 'white' }}>Item</TableCell>
                                                    <TableCell align="right" sx={{ color: 'white' }}>Qty</TableCell>
                                                    <TableCell align="right" sx={{ color: 'white' }}>Price</TableCell>
                                                    <TableCell align="right" sx={{ color: 'white' }}>Total</TableCell>
                                                    <TableCell align="right" sx={{ color: 'white' }}>Action</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {cart.map((item) => (
                                                    <TableRow key={item.id} hover>
                                                        <TableCell>{item.name || getCategoriesNameById(item.food_id, foodData)}</TableCell>
                                                        <TableCell align="right">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => adjustQuantity(item.id, -1)}
                                                                disabled={item.quantity <= 1}
                                                                color="secondary"
                                                            >
                                                                <Remove fontSize="small" />
                                                            </IconButton>
                                                            <span style={{ minWidth: '20px', display: 'inline-block' }}>{item.quantity}</span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => adjustQuantity(item.id, 1)}
                                                                color="secondary"
                                                            >
                                                                <Add fontSize="small" />
                                                            </IconButton>
                                                        </TableCell>
                                                        <TableCell align="right">{formatNumberWithComma(item.price)}</TableCell>
                                                        <TableCell align="right">{formatNumberWithComma(item.price * item.quantity)}</TableCell>
                                                        <TableCell align="right">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => removeFromCart(item.id)}
                                                                color="error"
                                                            >
                                                                <Delete fontSize="small" />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    
                                    {cart.length === 0 && (
                                        <Box sx={{ textAlign: 'center', py: 3 }}>
                                            <Typography variant="body2" color="textSecondary">
                                                Your cart is empty. Add products to continue.
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>

                                <Divider sx={{ my: 2 }} />

                                {/* Payment Section */}
                                <Box sx={{ mb: 3 }}>
                                    <Box sx={{
                                        backgroundColor: 'primary.light',
                                        p: 2,
                                        borderRadius: 2,
                                        mb: 2
                                    }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography sx={{ fontWeight: 'bold', color: 'white' }}>Total Amount:</Typography>
                                            <Typography sx={{ fontWeight: 'bold', color: 'white' }}>
                                                {formatNumberWithComma(calculateTotal())}
                                            </Typography>
                                        </Box>
                                        
                                        {selectedPendingOrder && (
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                                <Typography sx={{ color: 'white', fontSize: '0.875rem' }}>
                                                    Existing Order Total:
                                                </Typography>
                                                <Typography sx={{ color: 'white', fontSize: '0.875rem' }}>
                                                    {formatNumberWithComma(selectedPendingOrder.total_amount)}
                                                </Typography>
                                            </Box>
                                        )}
                                        
                                        {selectedPendingOrder && (
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                                <Typography sx={{ color: 'white', fontWeight: 'bold' }}>
                                                    Combined Total:
                                                </Typography>
                                                <Typography sx={{ color: 'white', fontWeight: 'bold' }}>
                                                    {formatNumberWithComma(calculateTotal() + (selectedPendingOrder?.total_amount || 0))}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {/* Checkout Button */}
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            size="large"
                            onClick={handleCheckout}
                            disabled={cart.length === 0 || processingOrder || (!formData.customer_name && !formData.customer_id)}
                            sx={{
                                mt: 1,
                                py: 1.5,
                                fontWeight: 'bold',
                                fontSize: '1rem'
                            }}
                        >
                            {processingOrder ? <CircularProgress size={24} /> : 
                                (selectedPendingOrder ? 'Add to Existing Order' : 'Complete Order')}
                        </Button>

                        {/* Print Receipt */}
                        <PrintReceipt
                            orderData={{
                                date: new Date().toISOString(),
                                customerName: formData.customer_name,
                                roomNumber: referenceData.roomNumbers.find(r => r.id == formData.room_number_id)?.room_number || referenceData.roomNumbers.find(r => r.id == formData.room_number_id)?.roomtype,
                                items: cart,
                                total: calculateTotal(),
                                orderType: selectedFoodType === 'ki' ? 'Kitchen' :
                                            selectedFoodType === 'ot' ? 'Other' : '',
                                servant: getCategoriesNameById(formData.servant_id, referenceData.employeeNames),
                                previousOrderNumber: formData.previousOrderNumber
                            }}
                            disabled={cart.length === 0}
                            sx={{ mt: 1 }}
                        />
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
};

export default Pos;